/**
 * The ONE renderer for a Coach's Tactics Board card.
 *
 * Pure, dependency-free SVG-string builders. This is the single source of
 * truth drawn by:
 *   - the live board (MiniPitch injects `cardInner` and adds pointer handlers),
 *   - the PNG export (serialises `cardSvg`),
 *   - the club viewer `packs/tactics-deck/page.html` (GENERATED from this file
 *     by frontend/scripts/tactics-pack.mjs, with a drift test).
 * Keeping it in one place is what stops the viewer and the board drifting —
 * every earlier review flagged that drift.
 *
 * Coordinates: players/ball/moves are CENTIMETRES from the centre spot, y up;
 * annotations are in SVG-view units in the deck's CANONICAL (un-flipped)
 * orientation — `cardInner` mirrors them with the pitch, so a drawing tracks
 * the players when the board or viewer is flipped. The board stores the
 * un-flipped point (it inverts the current flip when the coach draws).
 */

export const PITCH_L = 105
export const PITCH_W = 68
export const VIEW_W = PITCH_L + 6 // 111
export const VIEW_H = PITCH_W + 12 // 80 — extra bottom margin holds the legend
export const VIEW_BOX = `-3 -3 ${VIEW_W} ${VIEW_H}`
export const PITCH_GREEN = '#1f7a3d'
export const PLAYER_R = 1.3
export const BALL_R = 1.1

type XY = { x: number; y: number }
export type Anno =
  | { kind: 'arrow'; x1: number; y1: number; x2: number; y2: number }
  | { kind: 'zone'; x: number; y: number; w: number; h: number }
  | { kind: 'text'; x: number; y: number; text: string }
export type RenderPlayer = { team: number; jersey: number | string; x: number; y: number }
export type RenderCard = {
  players: RenderPlayer[]
  ball: XY | null
  moves?: { team: number; from: XY; to: XY }[]
  ballMove?: { from: XY; to: XY } | null
  caption?: string
  annos?: Anno[]
}
export type RenderTeams = {
  home: { name: string; color: string }
  away: { name: string; color: string }
}
/** Board-only layers; the viewer passes none of this. */
export type Overlay = {
  cells?: { x0: number; y0: number; w: number; h: number; team: number }[]
  blocks?: { team: number; hull: XY[] }[]
  lanes?: { from: XY; to: XY }[]
  carrier?: { team: number; jersey: number | string; x: number; y: number } | null
  press?: number
}
export type RenderOpts = {
  flipH?: boolean
  flipV?: boolean
  spotlight?: Set<string>
  overlay?: Overlay
}

/** Escape for text AND attribute context (quotes included) so an authored
 *  colour or name cannot break out of an SVG attribute. */
export function esc(v: unknown): string {
  return String(v == null ? '' : v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}
/** Coerce geometry to a finite number so a bad value renders as 0, never an
 *  injected attribute. */
export function num(v: unknown): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}
/** cm-from-centre (y up) → SVG metres, with the deck's flip orientation. */
export function svgPos(x: number, y: number, flipH = false, flipV = false): [number, number] {
  let px = PITCH_L / 2 + x / 100
  let py = PITCH_W / 2 - y / 100
  if (flipH) px = PITCH_L - px
  if (flipV) py = PITCH_W - py
  return [px, py]
}
/** Wrap a caption on word boundaries (SVG text does not wrap). */
export function wrapCaption(text: string, max = 46): string[] {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean)
  const lines: string[] = []
  let line = ''
  for (const w of words) {
    if (line && line.length + 1 + w.length > max) { lines.push(line); line = w }
    else line = line ? `${line} ${w}` : w
  }
  if (line) lines.push(line)
  return lines
}

/** A straight arrow: shaft shortened off the dot plus a filled head. */
function arrow(ax: number, ay: number, bx: number, by: number, color: string, dashed: boolean): string {
  const dx = bx - ax, dy = by - ay, len = Math.hypot(dx, dy) || 1, ux = dx / len, uy = dy / len
  const sx = ax + ux * 1.5, sy = ay + uy * 1.5, ex = bx - ux * 1.0, ey = by - uy * 1.0
  const h = 1.4, px = -uy, py = ux
  const head = `${ex},${ey} ${ex - ux * h + px * h * 0.6},${ey - uy * h + py * h * 0.6} `
    + `${ex - ux * h - px * h * 0.6},${ey - uy * h - py * h * 0.6}`
  const dash = dashed ? ' stroke-dasharray="1.4 1.2"' : ''
  return `<g stroke="${esc(color)}" fill="${esc(color)}" opacity="0.9">`
    + `<line x1="${sx}" y1="${sy}" x2="${ex}" y2="${ey}" stroke-width="0.5" stroke-linecap="round"${dash}/>`
    + `<polygon points="${head}" stroke="none"/></g>`
}

/** Field markings (Laws of the Game) in the 105×68 m frame. */
function pitchLines(): string {
  const s = 'fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="0.2"'
  const box = (x: number, w: number, h: number) =>
    `<rect x="${x}" y="${(PITCH_W - h) / 2}" width="${w}" height="${h}" ${s}/>`
  return `<rect x="0" y="0" width="105" height="68" ${s}/>`
    + `<line x1="52.5" y1="0" x2="52.5" y2="68" ${s}/>`
    + `<circle cx="52.5" cy="34" r="9.15" ${s}/>`
    + '<circle cx="52.5" cy="34" r="0.3" fill="rgba(255,255,255,0.55)" stroke="none"/>'
    + box(0, 16.5, 40.32) + box(88.5, 16.5, 40.32)
    + box(0, 5.5, 18.32) + box(99.5, 5.5, 18.32)
    + box(-1.5, 1.5, 7.32) + box(105, 1.5, 7.32)
}

function legend(teams: RenderTeams): string {
  // Clamp the home-name offset so a long club name cannot push the away chip
  // off the right edge of the 111-unit frame.
  const h = teams.home, a = teams.away, off = Math.min(3.4 + h.name.length * 1.15, 50)
  return '<g font-size="1.9" font-weight="bold" dominant-baseline="central">'
    + `<circle cx="2" cy="70" r="0.9" fill="${esc(h.color)}" stroke="#fff" stroke-width="0.18"/>`
    + `<text x="3.4" y="70" fill="#fff">${esc(h.name)}</text>`
    + `<circle cx="${off + 2.2}" cy="70" r="0.9" fill="${esc(a.color)}" stroke="#fff" stroke-width="0.18"/>`
    + `<text x="${off + 3.6}" y="70" fill="#fff">${esc(a.name)}</text></g>`
}

/** Mirror an annotation (stored un-flipped, in view units) to the card's
 *  orientation, so a drawing moves with the pitch under Flip ↔ / Flip ↕. */
export function flipAnno(an: Anno, fH: boolean, fV: boolean): Anno {
  if (!fH && !fV) return an
  const fx = (x: number) => (fH ? PITCH_L - num(x) : num(x))
  const fy = (y: number) => (fV ? PITCH_W - num(y) : num(y))
  if (an.kind === 'arrow') return { kind: 'arrow', x1: fx(an.x1), y1: fy(an.y1), x2: fx(an.x2), y2: fy(an.y2) }
  if (an.kind === 'zone') {
    // Mirror the far corner so the rect stays on the pitch, keeping w/h.
    const x = num(an.x), y = num(an.y), w = num(an.w), h = num(an.h)
    return { kind: 'zone', x: fH ? PITCH_L - (x + w) : x, y: fV ? PITCH_W - (y + h) : y, w, h }
  }
  return { kind: 'text', x: fx(an.x), y: fy(an.y), text: an.text }
}

function annoSvg(an: Anno): string {
  if (an.kind === 'arrow') return arrow(num(an.x1), num(an.y1), num(an.x2), num(an.y2), '#ffd166', false)
  if (an.kind === 'zone') {
    return `<rect x="${num(an.x)}" y="${num(an.y)}" width="${num(an.w)}" height="${num(an.h)}"`
      + ' rx="0.5" fill="#ffd166" opacity="0.18" stroke="#ffd166" stroke-width="0.3"/>'
  }
  return `<text x="${num(an.x)}" y="${num(an.y)}" font-size="3" font-weight="bold" fill="#ffd166"`
    + ` stroke="#000" stroke-width="0.12" paint-order="stroke">${esc(an.text)}</text>`
}

/** The card's inner SVG (everything inside `<svg>`), with the board's optional
 *  overlays, spotlight dimming and pressure ring. */
export function cardInner(card: RenderCard, teams: RenderTeams, opts: RenderOpts = {}): string {
  const fH = !!opts.flipH, fV = !!opts.flipV
  const spot = opts.spotlight, ov = opts.overlay ?? {}
  const col = (t: number) => (t === 1 ? teams.home.color : teams.away.color)
  const P = (x: number, y: number) => svgPos(x, y, fH, fV)

  let s = `<rect x="-3" y="-3" width="${VIEW_W}" height="${VIEW_H}" fill="${PITCH_GREEN}"/>` + pitchLines()

  // Pitch control (nearest-player space), beneath everything.
  for (const c of ov.cells ?? []) {
    const [ax, ay] = P(c.x0, c.y0), [bx, by] = P(c.x0 + c.w, c.y0 + c.h)
    s += `<rect x="${Math.min(ax, bx)}" y="${Math.min(ay, by)}" width="${Math.abs(bx - ax)}"`
      + ` height="${Math.abs(by - ay)}" fill="${esc(col(c.team))}" opacity="0.14"/>`
  }
  // Team blocks (convex hulls).
  for (const b of ov.blocks ?? []) {
    if (b.hull.length < 3) continue
    const pts = b.hull.map((pt) => { const [x, y] = P(pt.x, pt.y); return `${x},${y}` }).join(' ')
    s += `<polygon fill="none" stroke="${esc(col(b.team))}" stroke-width="0.4"`
      + ` stroke-dasharray="1.5 1" opacity="0.7" points="${pts}"/>`
  }
  // Open passing lanes.
  for (const ln of ov.lanes ?? []) {
    const [ax, ay] = P(ln.from.x, ln.from.y), [bx, by] = P(ln.to.x, ln.to.y)
    s += `<line x1="${ax}" y1="${ay}" x2="${bx}" y2="${by}" stroke="#ffe08a"`
      + ' stroke-width="0.4" stroke-dasharray="1.2 1" opacity="0.85"/>'
  }

  // Movement arrows (snapshotted; the board omits them while animating).
  for (const m of card.moves ?? []) {
    const [ax, ay] = P(m.from.x, m.from.y), [bx, by] = P(m.to.x, m.to.y)
    s += arrow(ax, ay, bx, by, col(m.team), false)
  }
  if (card.ballMove) {
    const [ax, ay] = P(card.ballMove.from.x, card.ballMove.from.y)
    const [bx, by] = P(card.ballMove.to.x, card.ballMove.to.y)
    s += arrow(ax, ay, bx, by, '#fff', true)
  }

  // Players (data-key for click delegation; spotlight dim; pressure ring).
  const carrierKey = ov.carrier ? `${ov.carrier.team}_${ov.carrier.jersey}` : null
  for (const p of card.players) {
    const [x, y] = P(p.x, p.y)
    const key = `${p.team}_${p.jersey}`
    const dim = spot && spot.size > 0 && !spot.has(key)
    let g = `<g data-key="${esc(key)}"${dim ? ' opacity="0.25"' : ''}>`
    if (key === carrierKey && (ov.press ?? -1) >= 0) {
      const pr = ov.press as number
      const c = pr < 300 ? '#ff5a5a' : pr < 700 ? '#ffd166' : '#8af08a'
      g += `<circle cx="${x}" cy="${y}" r="2.4" fill="none" stroke="${c}" stroke-width="0.4"/>`
    }
    g += `<circle cx="${x}" cy="${y}" r="${PLAYER_R}" fill="${esc(col(p.team))}" stroke="#fff" stroke-width="0.28"/>`
      + `<text x="${x}" y="${y}" font-size="1.5" font-weight="bold" fill="#fff"`
      + ` text-anchor="middle" dominant-baseline="central">${esc(p.jersey)}</text></g>`
    s += g
  }
  if (card.ball) {
    const [bx, by] = P(card.ball.x, card.ball.y)
    s += `<circle cx="${bx}" cy="${by}" r="${BALL_R}" fill="#fff" stroke="#111" stroke-width="0.3"/>`
  }

  const lines = wrapCaption(card.caption ?? '')
  if (lines.length) {
    s += `<rect x="${PITCH_L / 2 - 44}" y="1.5" width="88" height="${lines.length * 3.4 + 2}"`
      + ' rx="1" fill="rgba(0,0,0,0.55)"/>'
    lines.forEach((ln, i) => {
      s += `<text x="52.5" y="${4.6 + i * 3.4}" font-size="2.6" font-weight="bold" fill="#fff"`
        + ` text-anchor="middle">${esc(ln)}</text>`
    })
  }
  for (const an of card.annos ?? []) s += annoSvg(flipAnno(an, fH, fV))

  return s + legend(teams)
}

/** The whole `<svg>` for a card — used by the viewer and the PNG export. */
export function cardSvg(card: RenderCard, teams: RenderTeams, opts: RenderOpts = {}): string {
  return `<svg viewBox="${VIEW_BOX}" role="img" aria-label="Tactics card">`
    + cardInner(card, teams, opts) + '</svg>'
}
